import { NotificationsResponse } from '@app/AppContainer/components/Notifications/types';
import { generatedKeys } from '@app/data';
import { queryClient, useMutation } from '@app/QueryClient';
import {
	listNotifications,
	markAllAsRead,
	markAsRead,
} from '@app/Services/Notifications';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';

const { notifications } = generatedKeys;

export function useNotifications() {
	return useInfiniteQuery({
		queryKey: notifications?.list?.({}) as readonly (string | object)[],
		queryFn: ({ queryKey, pageParam }) =>
			listNotifications({
				queryKey: [
					queryKey?.[0] as string,
					{
						...(queryKey?.[1] as object),
						page: pageParam.toString(),
					},
				],
			}),
		staleTime: 5 * 1000 * 60, // 5 minutes
		initialPageParam: 1,
		getNextPageParam: (lastPage: {
			meta: { current_page: number; last_page: number };
		}) => {
			if (lastPage.meta.current_page < lastPage.meta.last_page) {
				return lastPage.meta.current_page + 1;
			}
			return undefined;
		},
	});
}

export function useMarkAsRead(opt?: { onSuccess?: () => void }) {
	const mutation = useMutation(markAsRead, {
		...opt,
		moduleName: 'common',
		operation: 'add',
		disableToaster: true,
		onSuccess: () => {
			opt?.onSuccess?.();
		},
		onMutate: (uuid) => {
			const previousNotifications = queryClient.getQueryData<
				InfiniteData<NotificationsResponse> | undefined
			>(notifications?.list?.({}) ?? ['']);

			queryClient.setQueryData<InfiniteData<NotificationsResponse> | undefined>(
				notifications?.list?.({}) ?? [''],
				(oldData) => {
					if (!oldData) return oldData;
					const newPages = oldData.pages.map((page) => {
						return {
							...page,
							data: page.data.map((notification) =>
								notification.uuid === uuid
									? { ...notification, seen: false }
									: notification
							),
						};
					});
					return {
						...oldData,
						pages: newPages,
					};
				}
			);

			return { previousNotifications };
		},
		onError: (_, __, context) => {
			queryClient.setQueryData<InfiniteData<NotificationsResponse> | undefined>(
				notifications?.list?.({}) ?? [''],
				context?.previousNotifications
			);
		},
	});

	const { mutateAsync } = mutation;

	return {
		...mutation,
		doMarkAsRead: mutateAsync,
	};
}

export function useMarkAllAsRead(opt?: { onSuccess?: () => void }) {
	const mutation = useMutation(markAllAsRead, {
		...opt,
		moduleName: 'common',
		operation: 'add',
		disableToaster: true,
		onSuccess: () => {
			opt?.onSuccess?.();
		},
		onMutate: () => {
			const previousNotifications = queryClient.getQueryData<
				InfiniteData<NotificationsResponse> | undefined
			>(notifications?.list?.({}) ?? ['']);

			queryClient.setQueryData<InfiniteData<NotificationsResponse> | undefined>(
				notifications?.list?.({}) ?? [''],
				(oldData) => {
					if (!oldData) return oldData;
					const newPages = oldData.pages.map((page) => {
						return {
							...page,
							data: page.data.map((notification) => ({
								...notification,
								seen: true,
							})),
							unread_count: 0,
						};
					});
					return {
						...oldData,
						pages: newPages,
					};
				}
			);

			return { previousNotifications };
		},
		onError: (_, __, context) => {
			queryClient.setQueryData<InfiniteData<NotificationsResponse> | undefined>(
				notifications?.list?.({}) ?? [''],
				context?.previousNotifications
			);
		},
	});

	const { mutateAsync } = mutation;

	return {
		...mutation,
		doMarkAllAsRead: mutateAsync,
	};
}
